<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PurchaseCreditsModal from './components/PurchaseCreditsModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const router = useRouter();
const { currentAccount, isOnChatwootCloud } = useAccount();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

const isWaitingForBilling = ref(false);
const purchaseCreditsModalRef = ref(null);
const selectedPlanCode = ref('');
const selectedBillingCycle = ref('monthly');
const selectedPaymentMode = ref('');

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

const billingState = computed(() => {
  return currentAccount.value.billing_state || {};
});

const billingCatalog = computed(() => {
  return currentAccount.value.billing_catalog || {};
});

const availablePlans = computed(() => {
  return billingCatalog.value.plans || [];
});

const selectedPlan = computed(() => {
  return (
    availablePlans.value.find(plan => plan.code === selectedPlanCode.value) ||
    availablePlans.value[0] ||
    null
  );
});

const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const effectivePlanName = computed(() => {
  return billingState.value.effective_plan_name || planName.value;
});

const paymentStatus = computed(() => {
  return billingState.value.subscription_status;
});

const accessPolicy = computed(() => {
  return billingState.value.access_policy;
});

const hasBillingCatalog = computed(() => {
  return availablePlans.value.length > 0;
});

const canPurchaseCredits = computed(() => {
  const plan = effectivePlanName.value?.toLowerCase();
  return plan && plan !== 'hacker';
});

const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

const hasABillingPlan = computed(() => {
  return !!effectivePlanName.value;
});

const fetchAccountDetails = async () => {
  await fetchLimits();
};

const handleBillingPageLogic = async () => {
  // If self-hosted, redirect to dashboard
  if (!isOnChatwootCloud.value) {
    router.push({ name: 'home' });
    return;
  }

  await fetchAccountDetails();
  sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
};

const syncSelectionFromAccount = () => {
  selectedPlanCode.value =
    billingState.value.selected_plan_code || availablePlans.value[0]?.code || '';
  selectedBillingCycle.value =
    billingState.value.billing_cycle ||
    selectedPlan.value?.billing_cycles?.[0]?.name ||
    'monthly';
  selectedPaymentMode.value =
    billingState.value.payment_mode ||
    selectedPlan.value?.payment_modes?.[0] ||
    '';
};

const billingPayload = computed(() => {
  return {
    provider_key: customAttributes.value.billing_provider || 'mercado_pago',
    plan_code: selectedPlanCode.value,
    billing_cycle: selectedBillingCycle.value,
    payment_mode: selectedPaymentMode.value,
  };
});

const onClickBillingPortal = async () => {
  if (!hasBillingCatalog.value) {
    return;
  }

  const hasCustomer =
    customAttributes.value.mercado_pago_customer_id ||
    customAttributes.value.stripe_customer_id;

  if (!hasCustomer) {
    isWaitingForBilling.value = true;
    sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);
    await store.dispatch('accounts/subscription', billingPayload.value);
    setTimeout(() => {
      window.location.reload();
    }, 5000);
    return;
  }

  await store.dispatch('accounts/checkout', billingPayload.value);
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const openPurchaseCreditsModal = () => {
  purchaseCreditsModalRef.value?.open();
};

const handleTopupSuccess = () => {
  fetchLimits();
};

watch(
  [availablePlans, billingState],
  () => {
    syncSelectionFromAccount();
  },
  { immediate: true }
);

watch(selectedPlan, plan => {
  if (!plan) {
    return;
  }

  const cycleNames = (plan.billing_cycles || []).map(cycle => cycle.name);
  const paymentModes = plan.payment_modes || [];

  if (!cycleNames.includes(selectedBillingCycle.value)) {
    selectedBillingCycle.value = cycleNames[0] || 'monthly';
  }

  if (!paymentModes.includes(selectedPaymentMode.value)) {
    selectedPaymentMode.value = paymentModes[0] || '';
  }
});

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem || isWaitingForBilling"
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="!hasBillingCatalog && !isWaitingForBilling"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="onClickBillingPortal">
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>
          <div
            v-if="hasBillingCatalog"
            class="grid lg:grid-cols-3 grid-cols-1 gap-3 px-5 pb-5"
          >
            <label class="grid gap-1">
              <span class="text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.SELECTORS.PLAN') }}
              </span>
              <select
                v-model="selectedPlanCode"
                class="border rounded-md px-3 py-2 bg-n-background"
              >
                <option
                  v-for="plan in availablePlans"
                  :key="plan.code"
                  :value="plan.code"
                >
                  {{ plan.name }}
                </option>
              </select>
            </label>
            <label class="grid gap-1">
              <span class="text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.SELECTORS.BILLING_CYCLE') }}
              </span>
              <select
                v-model="selectedBillingCycle"
                class="border rounded-md px-3 py-2 bg-n-background"
              >
                <option
                  v-for="cycle in selectedPlan?.billing_cycles || []"
                  :key="cycle.name"
                  :value="cycle.name"
                >
                  {{ $t(`BILLING_SETTINGS.CYCLES.${cycle.name.toUpperCase()}`) }}
                </option>
              </select>
            </label>
            <label class="grid gap-1">
              <span class="text-sm text-n-slate-11">
                {{ $t('BILLING_SETTINGS.SELECTORS.PAYMENT_MODE') }}
              </span>
              <select
                v-model="selectedPaymentMode"
                class="border rounded-md px-3 py-2 bg-n-background"
              >
                <option
                  v-for="paymentMode in selectedPlan?.payment_modes || []"
                  :key="paymentMode"
                  :value="paymentMode"
                >
                  {{
                    $t(
                      `BILLING_SETTINGS.PAYMENT_MODES.${paymentMode.toUpperCase()}`
                    )
                  }}
                </option>
              </select>
            </label>
          </div>
          <div
            v-if="
              hasABillingPlan ||
              subscribedQuantity ||
              subscriptionRenewsOn ||
              paymentStatus ||
              accessPolicy
            "
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="effectivePlanName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
            <DetailItem
              v-if="paymentStatus"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.PAYMENT_STATUS')"
              :value="paymentStatus"
            />
            <DetailItem
              v-if="accessPolicy"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.ACCESS_POLICY')"
              :value="accessPolicy"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="captainEnabled"
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
        >
          <template #action>
            <div class="flex gap-2">
              <ButtonV4
                sm
                flushed
                slate
                icon="i-lucide-refresh-cw"
                :is-loading="isFetchingLimits"
                @click="fetchLimits"
              >
                {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
              </ButtonV4>
              <ButtonV4
                v-if="canPurchaseCredits"
                sm
                solid
                blue
                @click="openPurchaseCreditsModal"
              >
                {{ $t('BILLING_SETTINGS.TOPUP.BUY_CREDITS') }}
              </ButtonV4>
            </div>
          </template>
          <div v-if="captainLimits && responseLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
              v-bind="responseLimits"
            />
          </div>
          <div v-if="captainLimits && documentLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
              v-bind="documentLimits"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-else
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
        >
          <template #action>
            <ButtonV4 sm solid slate @click="onClickBillingPortal">
              {{ $t('CAPTAIN.PAYWALL.UPGRADE_NOW') }}
            </ButtonV4>
          </template>
        </BillingCard>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
      <PurchaseCreditsModal
        ref="purchaseCreditsModalRef"
        @success="handleTopupSuccess"
      />
    </template>
  </SettingsLayout>
</template>
